import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const EmployerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [companyNotes, setCompanyNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [currentPage, statusFilter, searchTerm]);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getEmployerRequestStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmployerRequests(currentPage, pageSize, statusFilter, searchTerm);
      setRequests(response.data.requests);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load employer requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await adminAPI.updateEmployerRequestStatus(requestId, newStatus, adminNotes, companyNotes);
      toast.success(`Request status updated to ${newStatus}`);
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setCompanyNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update request status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkAsPending = async (requestId) => {
    setUpdatingStatus(true);
    try {
      await adminAPI.markEmployerRequestAsPending(requestId, adminNotes, companyNotes);
      toast.success('Request marked as contacted and email sent');
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setCompanyNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error('Error marking as pending:', error);
      toast.error('Failed to mark request as pending');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleApprove = async (requestId) => {
    setUpdatingStatus(true);
    try {
      await adminAPI.approveEmployerRequest(requestId, adminNotes, companyNotes);
      toast.success('Request approved, account created, and credentials sent');
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setCompanyNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this employer access request?')) {
      return;
    }

    setUpdatingStatus(true);
    try {
      await adminAPI.updateEmployerRequestStatus(requestId, 'rejected', adminNotes, companyNotes);
      toast.success('Request marked as rejected');
      setShowModal(false);
      setSelectedRequest(null);
      setAdminNotes('');
      setCompanyNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes || '');
    setCompanyNotes(request.companyNotes || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setAdminNotes('');
    setCompanyNotes('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'responded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl shadow-sm border border-primary-100">
            <h3 className="text-sm font-medium text-gray-600">Total Requests</h3>
            <p className="text-2xl font-bold text-primary-600">{stats.totalRequests}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-white p-4 rounded-xl shadow-sm border border-yellow-100">
            <h3 className="text-sm font-medium text-gray-600">Pending</h3>
            <p className="text-2xl font-bold text-yellow-600">{stats.pendingRequests}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-sm border border-blue-100">
            <h3 className="text-sm font-medium text-gray-600">Contacted</h3>
            <p className="text-2xl font-bold text-blue-600">{stats.contactedRequests}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-white p-4 rounded-xl shadow-sm border border-purple-100">
            <h3 className="text-sm font-medium text-gray-600">Responded</h3>
            <p className="text-2xl font-bold text-purple-600">{stats.respondedRequests || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-white p-4 rounded-xl shadow-sm border border-green-100">
            <h3 className="text-sm font-medium text-gray-600">Approved</h3>
            <p className="text-2xl font-bold text-green-600">{stats.approvedRequests}</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by company, email, or contact name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="responded">Responded</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No requests found</h3>
            <p className="mt-1 text-sm text-gray-500">No employer access requests match your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{request.companyName}</div>
                      {request.phone && (
                        <div className="text-sm text-gray-500">{request.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {request.contactFirstName} {request.contactLastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{request.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openModal(request)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal for Request Details */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={closeModal}>
          <div className="relative top-20 mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Employer Access Request</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Company Name</label>
                  <p className="text-gray-900">{selectedRequest.companyName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Contact Person</label>
                  <p className="text-gray-900">{selectedRequest.contactFirstName} {selectedRequest.contactLastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-gray-900">{selectedRequest.email}</p>
                </div>
              </div>

              {selectedRequest.phone && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-gray-900">{selectedRequest.phone}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedRequest.address}</p>
              </div>

              {selectedRequest.message && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Message</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Submitted On</label>
                <p className="text-gray-900">{formatDate(selectedRequest.createdAt)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Add notes about this request..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Notes</label>
                <textarea
                  value={companyNotes}
                  onChange={(e) => setCompanyNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                  placeholder="Company notes (read-only - updated by employer when they submit additional information)..."
                  disabled
                  readOnly
                />
                <p className="mt-1 text-xs text-gray-500">
                  This field is updated automatically when the employer provides additional information.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                <div className="flex gap-3 justify-between">
                  <button
                    onClick={() => {
                      if (selectedRequest.status === 'pending') {
                        // Save notes when clicking Contact
                        handleMarkAsPending(selectedRequest.id);
                      } else if (selectedRequest.status === 'responded') {
                        handleApprove(selectedRequest.id);
                      }
                    }}
                    disabled={
                      updatingStatus ||
                      !['pending', 'responded'].includes(selectedRequest.status)
                    }
                    className={`flex-1 px-4 py-2 rounded-md text-white font-medium transition-colors ${
                      selectedRequest.status === 'responded'
                        ? 'bg-green-600 hover:bg-green-700'
                        : selectedRequest.status === 'pending'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-300 cursor-not-allowed text-gray-600'
                    } ${updatingStatus ? 'opacity-50' : ''}`}
                  >
                    {selectedRequest.status === 'responded'
                      ? 'Approve'
                      : selectedRequest.status === 'pending'
                      ? 'Contact'
                      : 'No Action'}
                  </button>
                  <button
                    onClick={() => handleReject(selectedRequest.id)}
                    disabled={updatingStatus}
                    className="flex-1 px-4 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50 font-medium transition-colors disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
                {selectedRequest.status === 'pending' && (
                  <p className="mt-2 text-sm text-gray-500">
                    Choose <span className="font-medium text-blue-600">Contact</span> to email the employer and mark the request as contacted.
                  </p>
                )}
                {selectedRequest.status === 'responded' && (
                  <p className="mt-2 text-sm text-gray-500">
                    Choose <span className="font-medium text-green-600">Approve</span> to create the employer account and send credentials.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerRequests;





















